import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { MatDialogActions, MatDialogRef } from '@angular/material/dialog';

import { selectMemberProfile } from '@lib/members/data-access';
import {
  GenericServiceValue,
  PersonalDoctorMetadata,
  PersonalDoctorValue,
  PharmacologicalItemMetaData,
  PharmacologicalTreatmentValue,
  PrescriptionFormValue,
  PrescriptionItem,
  ServiceMetadata,
  processPrescriptionsDrafts,
  selectPrescriptionDrafts,
} from '@lib/prescriptions/data-access';

import { PrescriptionItemComponent } from '@lib/prescriptions/feature';

import { MemberProfile } from '@lib/core/models/member.interface';

@Component({
  selector: 'hm-process-prescription-drafts-modal',
  standalone: true,
  imports: [MatDialogActions, PrescriptionItemComponent],
  templateUrl: './process-prescription-drafts-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessPrescriptionDraftsModalComponent {
  private readonly store = inject(Store);
  private readonly dialogRef = inject(MatDialogRef<ProcessPrescriptionDraftsModalComponent>);

  protected readonly memberProfile = this.store.selectSignal<MemberProfile>(selectMemberProfile);
  protected readonly drafts = this.store.selectSignal<PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>[]>(selectPrescriptionDrafts);

  protected readonly newPrescriptionItems = computed(() => {
    return this.drafts()
      .filter(draft => !!draft.guaranteeId && !draft.originalPrescriptionId)
      .map(draft => this.convertFormValueToPrescriptionItem(draft));
  });
  protected readonly existingPrescriptionItems = computed(() => {
    return this.drafts()
      .filter(draft => !!draft.guaranteeId && !!draft.originalPrescriptionId)
      .map(draft => this.convertFormValueToPrescriptionItem(draft));
  });

  private convertFormValueToPrescriptionItem(
    draft: PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>
  ): PrescriptionItem<ServiceMetadata | PharmacologicalItemMetaData | PersonalDoctorMetadata> {
    return {
      id: draft.id,
      type: draft.type!,
      name: draft.name!,
      startDate: draft.startDate,
      endDate: draft.endDate,
      metadata: draft.metadata as ServiceMetadata | PharmacologicalItemMetaData | PersonalDoctorMetadata,
      guaranteeStatus: draft.guaranteeStatus,
      reason: draft.reason,
      sendGuaranteeNotification: draft.sendGuaranteeNotification!,
      franchise: draft.franchise,
      completedInAppointment: draft.completedInAppointment || false,
      completionReason: draft.completionReason,
      guaranteeId: draft.guaranteeId,
      originalPrescriptionId: draft.originalPrescriptionId,
    };
  }

  protected processDrafts(): void {
    const memberId = this.memberProfile().id;
    this.store.dispatch(processPrescriptionsDrafts({ memberId }));
    this.dialogRef.close();
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
